import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, CircularProgress, FormControl, Grid, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material';
import { GridDeleteIcon } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useDeleteMenuNetoMutation, useGetMenuNetosQuery } from './menuNetoApiSlice';
import ConfirmDialogList from '../../../../../components/ConfirmDialogList';
import configSiteUrls from '../../../configs/configSiteUrls';
import { ISmenuNeto, MenuNeto, MenuNetoList } from '../../../../../typesSite/menuneto/MenuNeto';

export const MenuNetoListForm = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [menuNetos, setMenuNetos] = useState<MenuNetoList[]>([]);

  const [menuNetoToDelete, setMenuNetoToDelete] = useState<MenuNeto>(ISmenuNeto);
  const [openConfirmExcluir, setOpenConfirmExcluir] = useState(false);
  const [deleteMenuNeto, { error: errorMenuNeto, isLoading, isSuccess }] = useDeleteMenuNetoMutation();

  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cursorPosition = useRef<number>(0);

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    cursorPosition.current = inputRef.current?.selectionStart || 0;
    setSearch(e.target.value);
  };

  useEffect(() => {
    // Limpar o timeout anterior
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // Configurar o timeout para o debounce
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 700); // Tempo de debounce (700ms)

    // Limpar o timeout quando o componente for desmontado ou o efeito for reiniciado
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [search]); // Dependência do useEffect: quando o 'search' mudar

  const { data, isFetching, error } = useGetMenuNetosQuery({
    search: debouncedSearch
  });

  useEffect(() => {
    if (data) {
      setMenuNetos(data);
    }
    if (error) {
      enqueueSnackbar('Registros não encontrados!', { variant: 'error' });
    } else if (isFetching) {
      enqueueSnackbar('Buscando registros...', { variant: 'info' });
    }

    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current?.setSelectionRange(cursorPosition.current, cursorPosition.current);
    }
  }, [data, error, isFetching, enqueueSnackbar]);

  const headerGerados = (
    <Typography variant="body2" color="textSecondary">
      MenuNetoe(s) encontrado(s): {menuNetos.length}
    </Typography>
  );

  const renderData = (rowData: MenuNetoList) => {
    const dataCriacao = rowData.dataHoraCriacao ? format(rowData.dataHoraCriacao, 'dd/MM/yyyy HH:mm:ss') : 'Data não disponível';
    return (
      <Typography color="secondary" variant="body2">
        {dataCriacao}
      </Typography>
    );
  };

  const renderUsuario = (rowData: MenuNetoList) => (
    <Typography color="secondary" variant="body2">
      {rowData.usuario?.nome || 'Usuário não disponível'}
    </Typography>
  );
  const renderDescricao = (rowData: MenuNetoList) => {
    let descricao: string = '';

    if (rowData.isEscolaLegislativo) {
      descricao = '(ESCOLA-LEG) ';
    }

    if (rowData.menuFilho?.menuPai?.label) {
      descricao = '(' + rowData.menuFilho.menuPai.label + ') ';
    }
    if (rowData.menuFilho?.label) {
      descricao += '(' + rowData.menuFilho.label + ') ';
    }
    descricao += rowData.label ? rowData.label : '';

    return (
      <Typography color="secondary" variant="body2">
        {descricao}
      </Typography>
    );
  };

  const renderOrdem = (rowData: MenuNetoList) => (
    <Typography color="secondary" variant="body2">
      {rowData.ordem || ''}
    </Typography>
  );

  const renderAction = (rowData: any) => {
    return (
      <Box sx={{ display: 'flex', gap: '8px' }}>
        <Tooltip title="Editar">
          <Link to={`${configSiteUrls.SITE_MENU_NETO_EDIT_LK}${rowData.id}`}>
            <IconButton color="secondary" aria-label="edit">
              <EditIcon />
            </IconButton>
          </Link>
        </Tooltip>

        <Tooltip title="Excluir">
          <IconButton color="secondary" aria-label="delete" onClick={() => handleExcluirtClick(rowData)}>
            <GridDeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const handleExcluirtClick = (rowData: any) => {
    setMenuNetoToDelete(rowData);
    setOpenConfirmExcluir(true);
  };
  const handleCloseDialog = () => {
    setOpenConfirmExcluir(false);
  };
  const handleConfirm = async () => {
    setOpenConfirmExcluir(false);
    await deleteMenuNeto({
      id: menuNetoToDelete.id ? menuNetoToDelete.id : ''
    });
  };

  useEffect(() => {
    if (isSuccess) {
      enqueueSnackbar('Registro deletado com sucesso!', { variant: 'success' });
      // fetchDados();
    }
    if (errorMenuNeto) {
      enqueueSnackbar('Registro não deletado!', { variant: 'error' });
    }
  }, [isSuccess, errorMenuNeto]);

  if (isFetching || loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <ConfirmDialogList
        open={openConfirmExcluir}
        handleClose={handleCloseDialog}
        title="Confirmar Ação"
        text={['Você tem certeza de que deseja excluir esse SubMenu 02?', menuNetoToDelete.label ? menuNetoToDelete.label : '']}
        handleDelete={() => {
          handleConfirm(); // Chama a função handleConfirm
          setOpenConfirmExcluir(false); // Fecha o diálogo
        }}
      />

<Paper sx={{ width: '100%', height: '100vh', padding: '12px', display: 'flex', flexDirection: 'column' }}>
        <Grid container padding={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Box display="flex">
                  <Typography color="secondary" variant="h6" gutterBottom>
                    Sub Menu nível 3
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      navigate(configSiteUrls.SITE_MENU_NETO_CREATE);
                    }}
                  >
                    Novo
                  </Button>

                  <Button variant="contained" color="secondary" onClick={() => navigate(-1)}>
                    Voltar
                  </Button>
                </Box>
              </Box>
            </FormControl>
          </Grid>

          <Grid item xs={12} mt={5}>
            <FormControl fullWidth>
              <TextField label="Digite para pesquisar" name="nome" onChange={handleChangeSearch} size="small" inputProps={{ maxLength: 100 }} value={search} inputRef={inputRef} />
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ flex: 1, minHeight: 0 }}>
  <DataTable
    value={menuNetos}
    paginator
    rows={25}
    rowsPerPageOptions={[10, 25, 50]}
    showGridlines
    size="small"
    dataKey="id"
    header={headerGerados}
    scrollable
    scrollHeight="flex"
    columnResizeMode="expand"
    resizableColumns
    className="custom-datatable"
  >
    <Column header="Data" body={renderData} sortable style={{ width: '20%' }} />
    <Column header="Ordem" body={renderOrdem} sortable />
    <Column header="Descrição" body={renderDescricao} sortable />
    <Column header="Ações" body={renderAction} style={{ width: '20%' }} />
  </DataTable>
</Box>

      </Paper>
    </>
  );
};
